import type {
  CommonResponseSetStudentAppSettingResponse,
  CommonResponseString,
  CommonResponseStudentAppSettingResponse,
  CommonResponseStudentDetailResponse,
  CreateStudentsByExcelData,
  CreateStudentsByExcelParams,
  DownloadStudentsExcelParams,
  GetStudents1Data,
  GetStudentsData,
  GetStudentsParams,
  GetStudentWithPhoneNumberGroupData,
  StudentAppSettingUpdateRequest,
  StudentCreateRequest,
  StudentDeleteAllRequest,
  StudentDeleteRequest,
  StudentSchoolTypeAndGradeUpdateRequest,
  StudentStatusUpdateRequest,
  StudentUpdateRequest,
  UpdateStudentAppSettingsData,
} from '~/api-spec/__generated__/main'
import { httpClient, type HttpFile, type RequestMeta } from '~/http/client'
import type { WithNullableOptionals } from '~/http/request'
import type { Page } from '~/schema/page'

import {
  adaptStudent,
  adaptStudentAppSetting,
  adaptStudentAppSettings,
  adaptStudentExcelResult,
  adaptStudentPhoneGroups,
  adaptStudentSummaries,
  adaptStudentSummaryPage,
  adaptUsableAttendanceCode,
  type StudentAppSettingWire,
} from './adapters'
import type {
  Student,
  StudentAppSetting,
  StudentExcelResult,
  StudentId,
  StudentPhoneGroup,
  StudentSummary,
} from './schemas'

export type {
  StudentCreateRequest,
  StudentDeleteAllRequest,
  StudentDeleteRequest,
  StudentSchoolTypeAndGradeUpdateRequest,
  StudentStatusUpdateRequest,
  StudentUpdateRequest,
}

export const getStudent = async (studentId: StudentId, meta?: RequestMeta): Promise<Student> => {
  const body = await httpClient().get<CommonResponseStudentDetailResponse>(
    `/students/${studentId}`,
    {
      meta,
    },
  )
  return adaptStudent(body.data)
}

export const getStudentAppSetting = async (
  studentId: StudentId,
  meta?: RequestMeta,
): Promise<StudentAppSetting> => {
  const body = await httpClient().get<CommonResponseStudentAppSettingResponse>(
    `/students/app-setting/${studentId}`,
    { meta },
  )
  return adaptStudentAppSetting(body.data)
}

// 로그인한 학원의 재원 학생 전체 설정. 단건과 같은 응답 클래스라 같은 adapter 를 거친다.
export const getStudentAppSettings = async (meta?: RequestMeta): Promise<StudentAppSetting[]> => {
  const body = await httpClient().get<CommonResponseSetStudentAppSettingResponse>(
    '/students/app-setting',
    { meta },
  )
  return adaptStudentAppSettings(body.data) ?? []
}

export const updateStudentSchoolTypeAndGrade = async (
  studentId: StudentId,
  body: StudentSchoolTypeAndGradeUpdateRequest,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<unknown>(`/students/${studentId}`, body, { meta })
}

export const createStudent = async (
  body: StudentCreateRequest,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<unknown>('/students', body, { meta })
}

export const updateStudent = async (
  studentId: StudentId,
  body: StudentUpdateRequest,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().put<unknown>(`/students/${studentId}`, body, { meta })
}

/** 재원·퇴원 상태 일괄 변경. 학생 id 묶음이나 필터(학교급·학년·이름 + 이전 상태) 중 하나로 대상을 고른다 */
export const updateStudentStatus = async (
  body: StudentStatusUpdateRequest,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<unknown>('/students', body, { meta })
}

export const deleteStudents = async (
  body: StudentDeleteRequest,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete<unknown>('/students', { data: body, meta })
}

/** 필터에 걸리는 학생 전체 삭제. id 묶음 없이 조건만 받는다 */
export const deleteAllStudents = async (
  body: StudentDeleteAllRequest,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<unknown>('/students/delete-all', body, { meta })
}

export const getUsableAttendanceCode = async (
  meta?: RequestMeta,
): Promise<Student['attendanceCode']> => {
  const body = await httpClient().get<CommonResponseString>('/students/attendance-code', { meta })
  return adaptUsableAttendanceCode(body.data)
}

/***************** 학생 목록과 일괄 처리 *****************/
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 student/controller/StudentController.kt·StudentAppSettingController.kt·
// request/StudentAppSettingUpdateRequest.kt·model/type/StudentAppSettingKey.kt 로 확인.

// 상태·이름·학교급·학년 조건은 서버가 nullable 로 받는다. 정렬(sort)은 `속성,방향` 문자열을 조건마다 하나씩 보낸다.
export type StudentListParams = WithNullableOptionals<GetStudentsParams>

/** 학원 학생을 조건으로 찾아 쪽 단위로 받는다 */
export const getStudents = async (
  params: StudentListParams,
  meta?: RequestMeta,
): Promise<Page<StudentSummary>> => {
  const body = await httpClient().get<GetStudentsData>('/students', { params, meta })
  return adaptStudentSummaryPage(body.data)
}

/** 학생 id 묶음으로 찾는다. 로그인한 학원 학생이 아닌 id 는 결과에서 빠진다 */
export const getStudentsByIds = async (
  studentIds: StudentId[],
  meta?: RequestMeta,
): Promise<StudentSummary[]> => {
  const body = await httpClient().get<GetStudents1Data>('/students/list', {
    params: { idList: studentIds },
    meta,
  })
  return adaptStudentSummaries(body.data) ?? []
}

/** 선생님이 맡은 학생을 학교급·학년으로 묶어 학생·보호자 전화번호와 함께 받는다 */
export const getStudentPhoneGroups = async (meta?: RequestMeta): Promise<StudentPhoneGroup[]> => {
  const body = await httpClient().get<GetStudentWithPhoneNumberGroupData>('/students/phone', {
    meta,
  })
  return adaptStudentPhoneGroups(body.data) ?? []
}

// 서버는 설정 키를 동적 키(@JsonAnySetter)로 받는데 스펙은 그것을 studentAppSettingStringMap 이라는 필드 하나로 그린다.
// 그래서 키는 응답 와이어에서 고른다. 서버가 아는 키는 아래 여덟이고, 모르는 키(lectureService 등)를 보내면 거부한다.
type _UpdatableAppSettingKey = Exclude<
  keyof StudentAppSettingWire,
  'studentId' | 'lectureService' | 'availableDailyWorksheet'
>
export type StudentAppSettingUpdateBody = Pick<StudentAppSettingUpdateRequest, 'studentId'> &
  Partial<Pick<StudentAppSettingWire, _UpdatableAppSettingKey>>

/** 학생앱 설정을 학생마다 바꾼다. 보내지 않은 키는 그대로 둔다 */
export const updateStudentAppSettings = async (
  body: StudentAppSettingUpdateBody[],
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().put<UpdateStudentAppSettingsData>('/students/app-setting', body, { meta })
}

// status 를 보내지 않으면 서버가 재원(ACTIVE)으로 등록한다
export type StudentExcelParams = CreateStudentsByExcelParams

/**
 * xlsFile 파트에 엑셀 파일을 담은 multipart 폼으로 학생을 일괄 등록한다.
 * 실패한 행이 하나라도 있으면 성공 응답 대신 오류 본문의 data 에 StudentExcelFailureRow 목록이 실린다.
 */
export const createStudentsByExcel = async (
  form: FormData,
  params: StudentExcelParams,
  meta?: RequestMeta,
): Promise<StudentExcelResult> => {
  const body = await httpClient().post<CreateStudentsByExcelData>('/students/xls', form, {
    params,
    meta,
  })
  return adaptStudentExcelResult(body.data)
}

/***************** 학생 엑셀 *****************/
/** 학생 일괄 등록 양식 */
export const getStudentImportTemplate = async (meta?: RequestMeta): Promise<HttpFile> =>
  httpClient().getFile('/students/xls', { meta })

export type StudentSheetParams = DownloadStudentsExcelParams

/** 조건에 맞는 학생 목록 엑셀 */
export const getStudentSheet = async (
  params: StudentSheetParams,
  meta?: RequestMeta,
): Promise<HttpFile> => httpClient().getFile('/students/download/xls', { params, meta })
